// 입자 풀(CPU 갱신 + InstancedMesh, M1 결정: WebGL2·WebGPU 경로 차이를 피한다).
// 가산 스파크(속도 방향으로 늘어남) / 불투명 파편·피 방울 / 바닥 피 데칼(성인 모드).
// 카메라는 회전하지 않으므로 빌보드 축이 고정이다(오른쪽 = +x, 위 = 피치 기울기). 프레임 루프에서 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { attribute, float, length, max, smoothstep, uv, vec2, vec3 } from 'three/tsl';
import { BUDGET, CAMERA } from '../shared/config';

const PITCH = (CAMERA.pitchDeg * Math.PI) / 180;
const CP = Math.cos(PITCH), SP = Math.sin(PITCH);
const DECALS = 48;

class Pool {
  readonly mesh: THREE.InstancedMesh;
  n = 0;
  onBloodLanding: ((x: number, z: number, size: number, direction: number) => void) | null = null;
  readonly px: Float32Array; readonly py: Float32Array; readonly pz: Float32Array;
  readonly vx: Float32Array; readonly vy: Float32Array; readonly vz: Float32Array;
  readonly age: Float32Array; readonly life: Float32Array;
  readonly w: Float32Array; readonly cr: Float32Array; readonly cg: Float32Array; readonly cb: Float32Array;
  readonly grav: Float32Array; readonly drag: Float32Array;
  readonly matureOnly: Uint8Array;

  constructor(readonly cap: number, mat: THREE.Material, readonly stretch: boolean) {
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.translate(0, stretch ? -0.5 : 0, 0); // 스파크: 머리가 입자 위치, 꼬리가 뒤로
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, new THREE.Color(1, 1, 1));
    (this.mesh.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.renderOrder = 21;
    const f = () => new Float32Array(cap);
    this.px = f(); this.py = f(); this.pz = f(); this.vx = f(); this.vy = f(); this.vz = f();
    this.age = f(); this.life = f(); this.w = f(); this.cr = f(); this.cg = f(); this.cb = f();
    this.grav = f(); this.drag = f();
    this.matureOnly = new Uint8Array(cap);
  }

  spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, w: number, r: number, g: number, b: number, grav: number, drag: number, matureOnly = false): void {
    if (this.n >= this.cap) return;
    const i = this.n++;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z;
    this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz;
    this.age[i] = 0; this.life[i] = life; this.w[i] = w;
    this.cr[i] = r; this.cg[i] = g; this.cb[i] = b;
    this.grav[i] = grav; this.drag[i] = drag;
    this.matureOnly[i] = matureOnly ? 1 : 0;
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.pz[i] = this.pz[j];
    this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.w[i] = this.w[j];
    this.cr[i] = this.cr[j]; this.cg[i] = this.cg[j]; this.cb[i] = this.cb[j];
    this.grav[i] = this.grav[j]; this.drag[i] = this.drag[j];
    this.matureOnly[i] = this.matureOnly[j];
  }

  update(dt: number, mature = true): void {
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const col = this.mesh.instanceColor!.array as Float32Array;
    let i = 0;
    while (i < this.n) {
      // 일시정지(dt=0)에서도 성인 전용 입자를 즉시 제거한다. 교환된 슬롯도 다시 검사한다.
      if (!mature && this.matureOnly[i]) { this.kill(i); continue; }
      if (dt > 0) {
        this.age[i] += dt;
        if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
        const k = Math.max(0, 1 - this.drag[i] * dt);
        this.vy[i] -= this.grav[i] * dt;
        this.vx[i] *= k; this.vy[i] *= k; this.vz[i] *= k;
        this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt;
        if (this.py[i] < 0.03 && this.grav[i] > 0) {
          if (this.matureOnly[i]) {
            this.onBloodLanding?.(this.px[i], this.pz[i], this.w[i] * 2.2, Math.atan2(this.vx[i], this.vz[i]));
            this.kill(i); continue;
          }
          this.py[i] = 0.03;
          this.vy[i] *= -0.3; this.vx[i] *= 0.6; this.vz[i] *= 0.6;
        }
      }
      const t = this.age[i] / this.life[i];
      const fade = 1 - t * t;
      // 화면 평면(오른쪽 R=(1,0,0), 위 U=(0,cp,-sp))에 속도를 투영해 긴 축을 잡는다
      const vr = this.vx[i], vu = this.vy[i] * CP - this.vz[i] * SP;
      const sp = Math.hypot(vr, vu);
      let lr = 0, lu = 1;
      if (sp > 1e-3) { lr = vr / sp; lu = vu / sp; }
      const w = this.stretch ? this.w[i] : this.w[i] * (0.4 + 0.6 * fade);
      const len = this.stretch ? w + sp * 0.035 : this.matureOnly[i] ? w + Math.min(sp, 6) * 0.018 : w * 1.25;
      const o = i * 16;
      m[o] = lu * w; m[o + 1] = -lr * CP * w; m[o + 2] = lr * SP * w; m[o + 3] = 0;
      m[o + 4] = lr * len; m[o + 5] = lu * CP * len; m[o + 6] = -lu * SP * len; m[o + 7] = 0;
      m[o + 8] = 0; m[o + 9] = SP; m[o + 10] = CP; m[o + 11] = 0;
      m[o + 12] = this.px[i]; m[o + 13] = this.py[i]; m[o + 14] = this.pz[i]; m[o + 15] = 1;
      const cf = this.stretch ? fade : 1;
      col[i * 3] = this.cr[i] * cf; col[i * 3 + 1] = this.cg[i] * cf; col[i * 3 + 2] = this.cb[i] * cf;
      i++;
    }
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
  }

  clear(): void {
    this.n = 0;
    this.mesh.count = 0;
  }
}

export class ParticleFx {
  readonly group = new THREE.Group();
  private readonly glow: Pool;
  private readonly solid: Pool;
  private readonly decals: THREE.InstancedMesh;
  private readonly decalAge = new Float32Array(DECALS).fill(1e9);
  private readonly decalX = new Float32Array(DECALS);
  private readonly decalZ = new Float32Array(DECALS);
  private readonly decalR = new Float32Array(DECALS);
  private readonly decalDir = new Float32Array(DECALS);
  private readonly decalOpacity = new THREE.InstancedBufferAttribute(new Float32Array(DECALS), 1);
  private decalNext = 0;
  private seed = 0x9e3779b9;
  /** QA 계측: 켜면 update에 쓴 CPU 시간(ms)을 cpuMs에 쌓는다(CombatFx가 ?qa에서 켠다) */
  profile = false;
  cpuMs = 0;

  constructor(particleScale: number) {
    const total = Math.floor(BUDGET.particles * particleScale);
    // 가산 스파크: 가로로 부드럽고, 꼬리로 갈수록 옅다
    const gm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    gm.fog = false;
    // 양면 투명은 기본 2회 그리기 — 가산 불똥은 한 번이면 된다
    gm.forceSinglePass = true;
    const across = uv().x.mul(2).sub(1).abs().oneMinus().pow(1.5);
    gm.colorNode = vec3(1, 1, 1).mul(across.mul(uv().y.pow(0.7)).mul(1.8));
    this.glow = new Pool(Math.floor(total * 0.75), gm, true);
    // 불투명 파편·피 방울: 둥근 조각
    const sm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    sm.forceSinglePass = true;
    const d = length(vec2(uv().x.mul(2).sub(1), uv().y.mul(2).sub(1)));
    sm.colorNode = vec3(1, 1, 1);
    sm.opacityNode = smoothstep(float(0.0), float(0.35), d.oneMinus());
    this.solid = new Pool(Math.floor(total * 0.25), sm, false);
    // 착지 자국은 작은 불규칙 방울 군집. 불투명 원판으로 경고 바닥을 덮지 않는다.
    const dg = new THREE.PlaneGeometry(2, 2);
    dg.rotateX(-Math.PI / 2);
    dg.setAttribute('bloodOpacity', this.decalOpacity);
    this.decalOpacity.setUsage(THREE.DynamicDrawUsage);
    const dm = new THREE.MeshBasicNodeMaterial({ color: 0x650d1c, transparent: true, depthWrite: false });
    const p = uv().sub(0.5).mul(2);
    const core = float(1).sub(smoothstep(0.48, 0.8, length(p)));
    const side = float(1).sub(smoothstep(0.12, 0.3, length(p.sub(vec2(0.55, 0.36)))));
    const tail = float(1).sub(smoothstep(0.09, 0.24, length(p.sub(vec2(-0.4, -0.62)))));
    dm.opacityNode = max(core, max(side, tail)).mul(attribute('bloodOpacity', 'float'));
    this.decals = new THREE.InstancedMesh(dg, dm, DECALS);
    this.solid.onBloodLanding = (x, z, size, direction) => this.decal(x, z, size, direction);
    this.decals.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.decals.frustumCulled = false;
    this.decals.count = 0;
    this.decals.renderOrder = 1;
    this.group.add(this.decals, this.solid.mesh, this.glow.mesh);
  }

  /** 결정적 난수(0..1) */
  private rnd(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  /** 타격 스파크: dir 방향(facing 규약) 부채꼴로 튄다. white = 흰 코어 비율 */
  sparks(x: number, y: number, z: number, dir: number, count: number, speed: number, white = 0.4): void {
    for (let i = 0; i < count; i++) {
      const a = dir + (this.rnd() - 0.5) * 2.4;
      const s = speed * (0.55 + this.rnd() * 0.7);
      const isWhite = this.rnd() < white;
      const r = isWhite ? 2.2 : 2.4, g = isWhite ? 2.0 : 0.35, b = isWhite ? 1.9 : 0.45;
      this.glow.spawn(x, y, z, Math.sin(a) * s, 1 + this.rnd() * 3.5, Math.cos(a) * s, 0.16 + this.rnd() * 0.18, 0.07 + this.rnd() * 0.05, r, g, b, 9, 2.5);
    }
  }

  /** 처치 파편(돌·흙 조각) */
  debris(x: number, z: number, dir: number, count: number, color = 0x6a5a44): void {
    const c = (color >> 16 & 255) / 255, g = (color >> 8 & 255) / 255, b = (color & 255) / 255;
    for (let i = 0; i < count; i++) {
      const a = dir + (this.rnd() - 0.5) * 3.0;
      const s = 3 + this.rnd() * 4;
      const sh = 0.7 + this.rnd() * 0.5;
      this.solid.spawn(x, 0.6, z, Math.sin(a) * s, 3 + this.rnd() * 3, Math.cos(a) * s, 0.55 + this.rnd() * 0.3, 0.1 + this.rnd() * 0.08, c * sh, g * sh, b * sh, 16, 1.0);
    }
  }

  /**
   * 색 지정 불똥(CombatFx): dir 방향(facing 규약) spread 범위로 튄다. r,g,b는 HDR 색(1 넘어도 됨),
   * white = 흰 코어 비율, life = 평균 수명(초), up = 위쪽 속도(m/s). count는 정수로 내린다.
   */
  streaks(x: number, y: number, z: number, dir: number, spread: number, count: number, speed: number, r: number, g: number, b: number, white: number, life: number, up: number): void {
    const n = count | 0;
    for (let i = 0; i < n; i++) {
      const a = dir + (this.rnd() - 0.5) * spread;
      const s = speed * (0.5 + this.rnd() * 0.75);
      const isWhite = this.rnd() < white;
      this.glow.spawn(x, y, z, Math.sin(a) * s, up * (0.3 + this.rnd() * 0.9), Math.cos(a) * s, life * (0.6 + this.rnd() * 0.7), 0.06 + this.rnd() * 0.05,
        isWhite ? 2.2 : r, isWhite ? 2.0 : g, isWhite ? 1.9 : b, 9, 2.5);
    }
  }

  /** 먼지 덩이(회피·착지) */
  dust(x: number, z: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const a = this.rnd() * Math.PI * 2;
      const s = 0.8 + this.rnd() * 1.4;
      this.solid.spawn(x, 0.15, z, Math.sin(a) * s, 0.6 + this.rnd(), Math.cos(a) * s, 0.4 + this.rnd() * 0.2, 0.22 + this.rnd() * 0.12, 0.72, 0.66, 0.56, 0, 3);
    }
  }

  /** 피 방울(성인 모드 전용, 호출하는 쪽이 settings.mature를 확인한다) */
  blood(x: number, y: number, z: number, dir: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const a = dir + (this.rnd() - 0.5) * 1.6;
      const s = 2.4 + this.rnd() * 2.4;
      const shade = 0.65 + this.rnd() * 0.35;
      this.solid.spawn(x, Math.min(y, 2.4), z, Math.sin(a) * s, 0.8 + this.rnd() * 1.2, Math.cos(a) * s, 0.9, 0.045 + this.rnd() * 0.04, 0.42 * shade, 0.012, 0.03, 18, 0.8, true);
    }
  }

  /** 바닥 피 웅덩이(성인 모드 전용) */
  decal(x: number, z: number, r: number, direction = 0): void {
    const i = this.decalNext;
    this.decalNext = (this.decalNext + 1) % DECALS;
    this.decalX[i] = x; this.decalZ[i] = z; this.decalR[i] = Math.min(0.55, r) * (0.8 + this.rnd() * 0.4);
    this.decalDir[i] = direction + (this.rnd() - 0.5) * 0.6;
    this.decalAge[i] = 0;
  }

  /** dtTicks: 영웅 시계 진행량(히트스톱·일시정지 0) */
  update(dtTicks: number, mature: boolean): void {
    const t0 = this.profile ? performance.now() : 0;
    const dt = dtTicks / 60;
    this.glow.update(dt);
    this.solid.update(dt, mature);
    if (!mature) {
      this.decalAge.fill(1e9);
      this.decals.count = 0;
      if (this.profile) this.cpuMs += performance.now() - t0;
      return;
    }
    const m = this.decals.instanceMatrix.array as Float32Array;
    let n = 0;
    for (let i = 0; i < DECALS; i++) {
      this.decalAge[i] += dt;
      const age = this.decalAge[i];
      if (age > 1.8) continue;
      const grow = 0.7 + Math.min(1, age / 0.08) * 0.3;
      this.decalOpacity.setX(n, 0.55 * Math.max(0, Math.min(1, (1.8 - age) / 0.7)));
      const r = this.decalR[i] * grow;
      const o = n * 16;
      const sn = Math.sin(this.decalDir[i]), cs = Math.cos(this.decalDir[i]);
      m[o] = cs * r * 0.7; m[o + 1] = 0; m[o + 2] = -sn * r * 0.7; m[o + 3] = 0;
      m[o + 4] = 0; m[o + 5] = 1; m[o + 6] = 0; m[o + 7] = 0;
      m[o + 8] = sn * r; m[o + 9] = 0; m[o + 10] = cs * r; m[o + 11] = 0;
      m[o + 12] = this.decalX[i]; m[o + 13] = 0.025; m[o + 14] = this.decalZ[i]; m[o + 15] = 1;
      n++;
    }
    this.decals.count = n;
    this.decals.instanceMatrix.needsUpdate = true;
    this.decalOpacity.needsUpdate = true;
    if (this.profile) this.cpuMs += performance.now() - t0;
  }

  /** 미리 컴파일용: 각 메시에 인스턴스 하나를 둔다. */
  prewarm(on: boolean): void {
    this.glow.mesh.count = on ? 1 : this.glow.n;
    this.solid.mesh.count = on ? 1 : this.solid.n;
    this.decals.count = on ? 1 : 0;
  }

  clear(): void {
    this.glow.clear();
    this.solid.clear();
    this.decalAge.fill(1e9);
    this.decals.count = 0;
  }

  dispose(): void {
    for (const m of [this.glow.mesh, this.solid.mesh, this.decals]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
      m.dispose();
    }
  }

  get activeCount(): number {
    return this.glow.n + this.solid.n;
  }
}
